import { v4 } from 'uuid'

import { ApiError } from '@app/packages/api/errors'
import { asError } from '@app/packages/asError/asError'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { profileUserResultSelector, profileUserSyncSlice } from '@app/store/selectors/profile'
import { failUserLoadSlice, finishUserLoadSlice, setUsersFromResponseSlice, startUserLoadSlice, usersModelsSelector } from '@app/store/slices/users'
import { createThunk } from '@app/store/thunk'
import type { StoreUser } from '@app/store/types/users'

import { callV2Api } from './api'

export const refreshUser = () =>
  createThunk(async dispatch => {
    dispatch(profileUserSyncSlice.set({ hash: v4() }))
    await dispatch(resolveThunkSelector(profileUserResultSelector))
    return v4()
  })

const fetchUserPromisesKey = Symbol('fetchUserPromises')

export function getFetchUserPromises() {
  return createThunk((_dispatch, _getState, context) => {
    return context.getRootContext().getOrInsert(fetchUserPromisesKey, () => new Map<string, Promise<StoreUser>>())
  })
}

/**
 * Reads a user unless one is already held complete, and shares an in-flight read by key so two
 * screens asking at once make one request.
 */
export function fetchUser(
  payload: { type: 'id'; value: string } | { type: 'token'; value: string } | { type: 'chat_uid'; value: string },
  { force = false, ensureAccountType }: { force?: boolean; ensureAccountType?: 'sitter' | 'parent' } = {}
) {
  return createThunk<Promise<StoreUser>>(async (dispatch, getState) => {
    const fetchUserPromises = dispatch(getFetchUserPromises())

    if (payload.value.includes('support')) throw ApiError.create(404, `Attempt to fetch user with id: ${payload.value}`, undefined)

    const held = usersModelsSelector(getState())[payload.value]

    if (!force && held && held.avatarsComplete) {
      if (ensureAccountType && held.account_type !== ensureAccountType) throw new Error('Invalid account type')
      return held
    }

    const promiseId = `${payload.type}:${payload.value}`

    const promise =
      fetchUserPromises.get(promiseId) ??
      (() => {
        const promise = dispatch(readUser(payload))
          .then(() => {
            const user = usersModelsSelector(getState())[payload.value]
            if (!user) throw new Error('User not found')
            if (ensureAccountType && user.account_type !== ensureAccountType) throw new Error('Invalid account type')
            return user
          })
          .finally(() => {
            fetchUserPromises.delete(promiseId)
          })

        fetchUserPromises.set(promiseId, promise)

        return promise
      })()

    return await promise
  })
}

/** One user read, keyed in the meta by whatever it was looked up by. */
function readUser(payload: { type: 'id' | 'token' | 'chat_uid'; value: string }) {
  return createThunk(async dispatch => {
    const { getUsersByChatUid, getUsersById, getUsersByToken } = await import('@app/packages/api/v2/actions/users')
    const action =
      payload.type === 'id' ? getUsersById(payload.value) : payload.type === 'chat_uid' ? getUsersByChatUid(payload.value) : getUsersByToken(payload.value)

    dispatch(startUserLoadSlice(payload.value))
    try {
      const resp = await dispatch(callV2Api(action))
      dispatch(setUsersFromResponseSlice(resp))
      dispatch(finishUserLoadSlice(payload.value))
      return resp
    } catch (e) {
      dispatch(failUserLoadSlice(payload.value, asError(e)))
      throw e
    }
  })
}
